import React, { useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Typography,
  Divider,
  Button,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { SubmitHandler, FormHandles, Scope } from '@unform/core';
import { Form } from '@unform/web';
import { differenceInHours } from 'date-fns';
import { FileObject } from 'material-ui-dropzone';
import { useStyles } from './styles';
import { RecordType, Agent, MissionCar } from '../../@types/records';
import { initialIncidentDetails } from './utils';
import { generateId } from '../AddEngineering/utils';
import LocalizationSection from '../../components/LocalizationFormGroup';
import ShowSnackbar, { SetSnackbarType } from '../../components/SnackBar';
import ViaFormGroup from '../../components/ViaFormGroup';
import VehicleFormGroup from '../../components/VehicleFormGroup';
import VictimFormGroup from '../../components/VictimFormGroup';
import AgentVehiclesFormGroup from '../../components/AgentsVehiclesFormGroup';
import {
  GoogleMapsReverseResponseType,
  GoogleMapsResultsType,
} from '../../@types/geocodingTypes';
import DialogWarningMessage from '../../components/DialogWarningMessage';
import DialogWarnActions, { DialogActionType } from '../../components/DialogWarnActions';
import IncidentDetailsForm from '../../components/IncidentDetailsForm';
import FileAttachmentFormGroup from '../../components/FileAttachmentFormGroup';

import { addImagesToIncident, createIncident } from '../../actions/incidentActions';
import {
  DriverIncidenteDetails,
  IncidentPostRequest,
  VehicleObject,
  ViaObject,
  VictimObject,
} from '../../@types/incidentTypes';

interface Coordinates {
  lat: number;
  lng: number;
}

const initialAdrees: GoogleMapsReverseResponseType = {
  status: '',
  plus_code: {
    compound_code: '',
    global_code: '',
  },
  results: [
    {
      address_components: [
        {
          long_name: '',
          short_name: '',
          types: [' '],
        },
      ],
      formatted_address: '',
      geometry: {
        location: {
          lat: 0,
          lng: 0,
        },
        location_type: '',
      },
    },
  ],
};

const initialAgents: Agent[] = [
  { Matrícula: '', _localId: generateId() },
  { Matrícula: '', _localId: generateId() },
  { Matrícula: '', _localId: generateId() },
  { Matrícula: '', _localId: generateId() },
];

const initialMissionCars: MissionCar[] = [
  { id: '', _localId: generateId() },
  { id: '', _localId: generateId() },
  { id: '', _localId: generateId() },
];

const genViaNames = (names: ViaObject[]) => {
  const viaNames = names.map((item, index) => item.Nome || `Via ${index + 1}`);
  viaNames.unshift('Não informado');
  return viaNames;
};

const AddIncident: React.FC = () => {
  const classes = useStyles();
  const [period, setPeriod] = useState('');
  const [latlng, setLatLng] = useState<Coordinates>({ lat: 0, lng: 0 });
  const [location, setLocation] = useState<GoogleMapsReverseResponseType>(initialAdrees);
  const [loading, setLoading] = useState<boolean>(false);
  const [snackBar, setSnackbar] = useState<SetSnackbarType>({ open: false, message: '' });
  const [driverIncidentDetails, setDriverIncidentDetails] = useState<
    DriverIncidenteDetails
  >(initialIncidentDetails);
  const [driverVias, setDriverVias] = useState<ViaObject[]>([]);
  const [driverVehicles, setDriverVehicles] = useState<VehicleObject[]>([]);
  const [driverVictims, setDriverVictims] = useState<VictimObject[]>([]);
  const [reference, setReference] = useState<string>('');
  const [adresses, setAdresses] = useState<{
    address1: string;
    address2: string;
    neighborhood: string;
  }>({
    address1: '',
    address2: '',
    neighborhood: '',
  });
  const [agentAndVehicles, setAgentsAndVehicles] = useState<{
    agents: Agent[];
    missionCars: MissionCar[];
    _localId: string;
  }>({ agents: initialAgents, missionCars: initialMissionCars, _localId: generateId() });

  const [mergeId, setMergeId] = useState<string>('');
  const [forcedPost, setForcedPost] = useState<boolean>(false);
  const [requiredFields, setRequiredFields] = useState<
    { key: string; message: string }[]
  >([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogAction, setDialogAction] = useState<DialogActionType>({
    open: false,
    title: '',
    actions: [],
  });
  const [files, setFiles] = useState<FileObject[]>([]);
  const [fileDescription, setFileDescription] = useState<string[]>([]);

  const uploadImages = useCallback(
    async (recordID: string) => {
      addImagesToIncident(recordID, files, fileDescription).catch(() => {
        console.log('Erro ao adicionar imagens ao sinistro.');
      });
    },
    [files, fileDescription],
  );

  const formRef = useRef<FormHandles>(null);
  const history = useHistory();
  const navigate = (path: string) => {
    history.push(path);
  };

  const postRecord = async (record: IncidentPostRequest) => {
    setLoading(true);
    setForcedPost(false);
    try {
      const { data } = await createIncident(record);

      const uuid = typeof data === 'string' ? data : data.uuid;

      if (files.length > 0 && uuid) {
        uploadImages(uuid);
      }

      setLoading(false);

      if (typeof data === 'string') {
        setDialogAction({
          open: true,
          title: 'Sinistro foi registrado com sucesso!',
          actions: [
            {
              btnText: 'Registros',
              color: 'primary',
              action: () => navigate('/registros/sinistros'),
            },
            {
              btnText: 'Mapa',
              color: 'primary',
              action: () => navigate('/mapa/sinistros'),
            },
            {
              btnText: 'Novo Sinistro',
              color: 'primary',
              action: () => window.location.reload(),
            },
          ],
        });

        return;
      }

      setDialogAction({
        open: true,
        title: 'Sinistro foi registrado com sucesso!',
        subtitle: 'Porém, apresenta duplicidades...',
        actions: [
          {
            btnText: 'Verificar Duplicidades',
            color: 'primary',
            action: () => navigate(`/registros/duplicidades/${data.uuid}`),
          },
          {
            btnText: 'Novo Sinistro',
            color: 'primary',
            action: () => window.location.reload(),
          },
        ],
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao adicionar sinistro.',
      });
    } finally {
      setLoading(false);
    }
  };

  const validateFields = (fieldsData: IncidentPostRequest) => {
    setRequiredFields([]);
    const { data } = fieldsData;
    // Mensagem de erro ao tratar os dados
    const fieldsWarn: { key: string; message: string }[] = [];
    if (!fieldsData.occurred_from) {
      fieldsWarn.push({
        key: 'incidentDate',
        message: 'A data e hora do sinistro são obrigatórias.',
      });
    }

    if (differenceInHours(Date.now(), new Date(fieldsData.occurred_from)) < 0) {
      fieldsWarn.push({
        key: 'incidentDate',
        message: 'A data e hora do sinistro ocorrer no futuro.',
      });
    }

    if (fieldsData.data.driverIncidenteDetails['Endereço 1'] === 'Não informado') {
      fieldsWarn.push({ key: 'Endereço 1', message: 'O endereço 1 é obrigatório.' });
    }

    if (data.driverVia.length > 0) {
      data.driverVia.map((item, index) => {
        if (!item.Nome) {
          fieldsWarn.push({
            key: `nomeVia ${index}`,
            message: `O nome da Via ${index + 1} é obrigatório.`,
          });
        }
      });
    }

    if (data.driverVitima.length > 0) {
      data.driverVitima.map((item) => {
        if (!item.Nome) item.Nome = 'Desconhecido';
        if (driverVehicles.length === 0) item['Código Veículo'] = 'Nenhum';
        if (!item['Data de Nascimento']) item['Data de Nascimento'] = '';
        if (!item['Data do Óbito']) item['Data do Óbito'] = '';
        if (!item['Data de Internação']) item['Data de Internação'] = '';
        if (!item['Fim da Reabilitação']) item['Fim da Reabilitação'] = '';
        if (!item['Início de Reabilitação']) item['Início de Reabilitação'] = '';
        if (!item['Data de Alta']) item['Data de Alta'] = '';
      });
    }

    if (fieldsWarn.length > 0) {
      setRequiredFields(fieldsWarn);
      setOpenDialog(true);
      return false;
    }

    fieldsData.data.driverIncidenteDetails['Hora informada'] = 'Sim';

    postRecord(fieldsData);
    window.scrollTo(0, 0);
    return true;
  };

  const handleSubmit: SubmitHandler<IncidentPostRequest> = async (fieldsData) => {
    const bodyForm: IncidentPostRequest = { ...fieldsData };

    const viaList = [...driverVias];

    const vehiclesList = [...driverVehicles];

    const victimsList = [...driverVictims];

    // Navegando na Array de Vítimas
    for (let i = 0; i < victimsList.length; i += 1) {
      // Setando o Perfil do Usuário
      if (victimsList[i]['Tipo de Pessoa'] === 'Ciclista') {
        victimsList[i]['Perfil do Usuário'] = 'Ciclista';
      } else if (victimsList[i]['Tipo de Pessoa'] === 'Motociclista') {
        victimsList[i]['Perfil do Usuário'] = 'duas_rodas';
      } else if (victimsList[i]['Tipo de Pessoa'] === 'Condutor') {
        victimsList[i]['Perfil do Usuário'] = 'quatro_rodas';
      } else if (victimsList[i]['Tipo de Pessoa'] === 'Pedestre') {
        victimsList[i]['Perfil do Usuário'] = 'Pedestre';
      } else if (victimsList[i]['Tipo de Pessoa'] === 'Outros') {
        victimsList[i]['Perfil do Usuário'] = 'Outros';
      }

      // Setando a Severidade do Acidente
      if (victimsList[i]['Gravidade da Lesão'] === 'Fatal') {
        driverIncidentDetails.Severidade = 'Fatal';
        break;
      } else if (
        victimsList[i]['Gravidade da Lesão'] === 'Grave' ||
        victimsList[i]['Gravidade da Lesão'] === 'Moderado'
      ) {
        driverIncidentDetails.Severidade = 'Ferido';
      } else if (
        victimsList[i]['Gravidade da Lesão'] === 'Leve' ||
        victimsList[i]['Gravidade da Lesão'] === 'Ileso'
      ) {
        if (driverIncidentDetails.Severidade === 'Ferido') {
          return;
        }
        driverIncidentDetails.Severidade = 'Ileso';
      }
    }

    bodyForm.schema = `${process.env.REACT_APP_INCIDENT_SCHEMA_ID}`;
    bodyForm.geom = `POINT(${latlng.lng} ${latlng.lat})`;
    bodyForm.occurred_from = period;
    bodyForm.occurred_to = period;
    bodyForm.data.driverIncidenteDetails = driverIncidentDetails;
    bodyForm.data.driverIncidenteDetails['Numero Referencia'] = reference;
    bodyForm.data.driverIncidenteDetails['Endereço 1'] = adresses.address1;
    bodyForm.data.driverIncidenteDetails['Endereço 2'] = adresses.address2;
    bodyForm.location_text = location.results[0].formatted_address || '';

    if (location.results[0] && location.results[0].address_components.length >= 0) {
      location.results.forEach((item: GoogleMapsResultsType) => {
        item.address_components.forEach((i) => {
          i.types.forEach((t) => {
            if (t === 'route') {
              bodyForm.road = i.long_name || '';
            }
            if (t === 'administrative_area_level_2') {
              bodyForm.city = i.long_name || '';
            }
            if (t === 'administrative_area_level_1') {
              bodyForm.state = i.long_name || '';
            }
          });
        });
      });
    }

    const intersection = bodyForm.data.driverIncidenteDetails.Interseção;

    intersection === 'Não' || intersection === 'Não informado'
      ? (bodyForm.data.driverIncidenteDetails['Ocorrido ao Longo da Via/Cruzamento'] =
          'Longo da via')
      : (bodyForm.data.driverIncidenteDetails['Ocorrido ao Longo da Via/Cruzamento'] =
          'Cruzamento');

    bodyForm.data.driverIncidenteDetails.Bairro = adresses.neighborhood;
    bodyForm.data.driverVia = viaList;
    bodyForm.data.driverVeiculo = vehiclesList;
    bodyForm.data.driverVitima = victimsList;
    bodyForm.data.driverAgentsAndVehicles = agentAndVehicles;

    for (let i = 0; i < bodyForm.data.driverVeiculo.length; i += 1) {
      bodyForm.data.driverVeiculo[i]._localId = generateId();
    }
    for (let i = 0; i < bodyForm.data.driverVia.length; i += 1) {
      bodyForm.data.driverVia[i]._localId = generateId();
    }
    for (let i = 0; i < bodyForm.data.driverVitima.length; i += 1) {
      bodyForm.data.driverVitima[i]._localId = generateId();
    }
    bodyForm.data.driverIncidenteDetails._localId = generateId();

    if (mergeId) bodyForm.uuid_merge = mergeId;

    if (forcedPost) bodyForm.force = 1;

    validateFields(bodyForm);
  };

  return (
    <Container className={classes.root}>
      <Typography variant="h5">Formulário de Entrada de Sinistro</Typography>
      <Divider />
      <Form
        id="incident-form"
        className={classes.formGroup}
        autoComplete="off"
        ref={formRef}
        onSubmit={handleSubmit}
      >
        <Grid container xs={12}>
          <Grid container spacing={2} sm={12} md={10}>
            <LocalizationSection
              setLocation={setLocation}
              latlng={latlng}
              setLatLng={setLatLng}
              period={period}
              setPeriod={setPeriod}
              requiredFields={requiredFields}
              adresses={adresses}
              setAdresses={setAdresses}
              reference={reference}
              setReference={setReference}
            />

            <Grid item sm={12} xs={12}>
              <Accordion className={classes.accordionContainer}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Detalhes do Sinistro</Typography>
                </AccordionSummary>
                <AccordionDetails style={{ display: 'flex', flexDirection: 'column' }}>
                  <Scope path="data.driverIncidenteDetails">
                    <IncidentDetailsForm
                      incidentDetails={driverIncidentDetails}
                      setIncidentDetails={setDriverIncidentDetails}
                    />
                  </Scope>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item sm={12} xs={12}>
              <ViaFormGroup
                driverVias={driverVias}
                setDriverVias={setDriverVias}
                requiredFields={requiredFields}
                adresses={adresses}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <VehicleFormGroup
                driverVehicles={driverVehicles}
                setDriverVehicles={setDriverVehicles}
                viaNames={genViaNames(driverVias)}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <VictimFormGroup
                driverVictims={driverVictims}
                setDriverVictims={setDriverVictims}
                vehicles={driverVehicles}
                requiredFields={requiredFields}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <FileAttachmentFormGroup
                files={files}
                setFiles={setFiles}
                fileDescriptions={fileDescription}
                setFileDescription={setFileDescription}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <AgentVehiclesFormGroup
                agentsAndVehicles={agentAndVehicles}
                setAgentsAndVehicles={setAgentsAndVehicles}
              />
            </Grid>
          </Grid>
          <Grid>
            <div className={classes.buttonsContainer}>
              <div className={classes.buttonWrapper}>
                <Button
                  className={classes.saveIncident}
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                >
                  Salvar Sinistro
                </Button>
                {loading && (
                  <CircularProgress size={24} className={classes.buttonProgress} />
                )}
              </div>
              <div className={classes.buttonWrapper}>
                <Button
                  className={classes.cancelIncident}
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate('/registros/sinistros')}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                {loading && (
                  <CircularProgress
                    size={24}
                    color="secondary"
                    className={classes.buttonProgress}
                  />
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </Form>
      <ShowSnackbar
        open={snackBar.open}
        message={snackBar.message}
        setSnackbar={setSnackbar}
      />

      <DialogWarningMessage
        title="Algumas informações precisam de atenção."
        messages={requiredFields}
        open={openDialog}
        setOpen={setOpenDialog}
      />

      <DialogWarnActions dialogActions={dialogAction} setDialog={setDialogAction} />
    </Container>
  );
};

export default AddIncident;
